import { useEffect, useRef, useState, type RefObject } from 'react';
import { requestLegalSdkToken } from '@api/Legal/LegalVerification';
import {
  getLegalSdkEnvironment,
  getLegalSdkSupport,
  loadLegalSdk,
  LEGAL_SDK_PRIMARY_COLOR,
  watchLegalSdkFirstRender,
  type LegalSdkHandle,
} from '@utils/legalSdk';
import {
  classifyLegalTokenError,
  type LegalTokenError,
} from '@components/verification/legalVerificationStatus';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseLegalSdkMountOptions {
  containerRef: RefObject<HTMLElement | null>;
  /**
   * El primer token abre un trámite nuevo (solo tras `closed`). Se lee al
   * montar: cambiarlo no vuelve a montar el SDK; para eso, `key` en quien lo usa.
   */
  newVerification: boolean;
  /** Pista para la UI; el estado real lo trae `get-profile`. */
  onStatusChange: (status: string) => void;
  onComplete: () => void;
  /** 400 1188: el panel ya está activo. */
  onAlreadyActive: () => void;
  /** 400 1189: el trámite nuevo ya no corresponde (el último no está `closed`). */
  onNewVerificationRejected: () => void;
}

/**
 * `loading`: script y primer token. `mounting`: el SDK ya está montado pero
 * sigue en su propia carga. `ready`: el SDK muestra el trámite.
 */
export type LegalSdkMountState = 'loading' | 'mounting' | 'ready' | 'error';

export interface UseLegalSdkMountResult {
  state: LegalSdkMountState;
  error: LegalTokenError | null;
  retry: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Monta el SDK de Legal en `containerRef` y lo desmonta al salir. El primer
 * token se pide antes de montar, en paralelo con el script: así los errores del
 * backend (1186–1192) se muestran en el modal con su salida propia en vez de la
 * pantalla genérica del SDK. Los siguientes los pide el SDK cuando vence el
 * anterior (30 min); si fallan, el SDK pide recargar (`TOKEN_UNAVAILABLE`).
 */
export const useLegalSdkMount = ({
  containerRef,
  newVerification,
  onStatusChange,
  onComplete,
  onAlreadyActive,
  onNewVerificationRejected,
}: UseLegalSdkMountOptions): UseLegalSdkMountResult => {
  const [state, setState] = useState<LegalSdkMountState>('loading');
  const [error, setError] = useState<LegalTokenError | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Los callbacks cambian en cada render del modal: se leen del ref para no
  // desmontar y volver a montar el SDK (se perderían los archivos elegidos).
  const callbacksRef = useRef({
    onStatusChange,
    onComplete,
    onAlreadyActive,
    onNewVerificationRejected,
  });
  callbacksRef.current = {
    onStatusChange,
    onComplete,
    onAlreadyActive,
    onNewVerificationRejected,
  };
  const newVerificationRef = useRef(newVerification);
  newVerificationRef.current = newVerification;

  // Montar un widget externo y desmontarlo al salir: side effect real.
  useEffect(() => {
    let isCancelled = false;
    let handle: LegalSdkHandle | null = null;
    let stopWatchingFirstRender: (() => void) | null = null;

    // Una respuesta que llega tarde, de un montaje ya descartado (modal
    // cerrado o reabierto), no puede cerrar ni refrescar nada.
    const reportError = (cause: unknown): void => {
      if (isCancelled) return;
      const tokenError = classifyLegalTokenError(cause);
      if (tokenError.kind === 'already_active') {
        callbacksRef.current.onAlreadyActive();
        return;
      }
      if (tokenError.kind === 'new_verification_rejected') {
        callbacksRef.current.onNewVerificationRejected();
        return;
      }
      setError(tokenError);
      setState('error');
    };

    setState('loading');
    setError(null);

    Promise.all([
      loadLegalSdk(),
      requestLegalSdkToken(
        newVerificationRef.current ? { new_verification: true } : {},
      ),
    ])
      .then(([sdk, firstToken]) => {
        const container = containerRef.current;
        if (isCancelled || !container) return;

        let pendingToken: string | null = firstToken;
        handle = sdk.mount(container, {
          environment: getLegalSdkEnvironment(),
          primaryColor: LEGAL_SDK_PRIMARY_COLOR,
          support: getLegalSdkSupport(),
          getToken: async () => {
            if (pendingToken) {
              const token = pendingToken;
              pendingToken = null;
              return token;
            }
            try {
              // Sin `new_verification`: el trámite ya existe y se retoma.
              return await requestLegalSdkToken();
            } catch (cause) {
              if (
                !isCancelled &&
                classifyLegalTokenError(cause).kind === 'already_active'
              ) {
                callbacksRef.current.onAlreadyActive();
              }
              throw cause;
            }
          },
          onStatusChange: (status) =>
            callbacksRef.current.onStatusChange(status),
          onComplete: () => callbacksRef.current.onComplete(),
          // Solo el código: el `message` puede traer datos del usuario.
          onError: ({ code }) => console.warn('[LegalSDK]', code),
        });
        setState('mounting');
        stopWatchingFirstRender = watchLegalSdkFirstRender(container, () => {
          if (!isCancelled) setState('ready');
        });
      })
      .catch(reportError);

    return () => {
      isCancelled = true;
      stopWatchingFirstRender?.();
      handle?.unmount();
    };
  }, [containerRef, attempt]);

  return { state, error, retry: () => setAttempt((value) => value + 1) };
};
